import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import {
    CartDefaults,
    CartItem,
    CartState,
    CartStatus,
    CleanCartResult,
    ClearCartResponsePayload,
    ItemOptionInfo,
    ItemOptionValue,
} from "@eshop/types/cart.types.ts";
import { logError, logInfo } from "@tools/LogsHandler.tsx";
import { Item } from "@eshop/types/catalogue.types.ts";
import { RootState } from "@store/store.tsx";
import { ApiErrorDetail, ErrorResponseState } from "@tools/types/common.types.ts";
import { isAxiosError } from 'axios';
import { openNotificationWithIcon, parsePrice } from '@tools/utils/common.tsx';
import { Button } from "antd";
import { conns } from '@cfg/axiosConfig.tsx';

const normalizeOptionValue = (value: unknown): string => {
    if (value === undefined || value === null) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch (error) {
        logError('No se pudo normalizar opción del carrito', error);
        return String(value);
    }
};

const sanitizeOptionArray = (options: ItemOptionInfo[] | null | undefined): ItemOptionInfo[] => {
    if (!options || options.length === 0) {
        return [];
    }
    return options.map((opt) => ({
        code: opt.code,
        value: opt.value ?? '',
        verbose: opt.verbose ?? opt.value ?? '',
        name: opt.name,
        format: opt.format,
        type: opt.type,
        required: opt.required,
        max_length: opt.max_length,
    }));
};

const buildCartItemOptions = (item: Item, rawOptions: unknown): ItemOptionInfo[] => {
    if (!rawOptions) {
        return [];
    }

    if (Array.isArray(rawOptions)) {
        return sanitizeOptionArray(rawOptions as ItemOptionInfo[]);
    }

    if (typeof rawOptions === 'object') {
        const valueMap = rawOptions as ItemOptionValue;
        const metaByCode = new Map((item.options ?? []).map((opt) => [opt.code, opt]));
        return Object.entries(valueMap).map(([code, value]) => {
            const meta = metaByCode.get(code);
            const normalizedValue = normalizeOptionValue(value);
            return {
                code,
                value: normalizedValue,
                verbose: normalizedValue,
                name: meta?.name,
                format: meta?.format,
                type: meta?.type,
            };
        });
    }

    return [];
};

const canonicalizeOptions = (options?: ItemOptionInfo[] | null): string => {
    const prepared = (options ?? []).map((opt) => ({
        code: opt.code,
        value: opt.value ?? '',
        verbose: opt.verbose ?? opt.value ?? '',
    }));
    prepared.sort((a, b) => a.code.localeCompare(b.code));
    return JSON.stringify(prepared);
};

const optionsAreEqual = (
    a?: ItemOptionInfo[] | null,
    b?: ItemOptionInfo[] | null,
): boolean => canonicalizeOptions(a) === canonicalizeOptions(b);

export const extractStockErrorDetail = (payload: unknown): ApiErrorDetail | null => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const responseData = (payload as ErrorResponseState)?.response?.data ?? payload;
    if (!responseData || typeof responseData !== 'object') {
        return null;
    }
    if ('code' in responseData && (responseData as ApiErrorDetail).code === 'out_of_stock') {
        return responseData as ApiErrorDetail;
    }
    return null;
};

export const notifyStockBlocked = (detail: ApiErrorDetail) => {
    const description = detail.message
        ?? 'No hay stock suficiente para completar la acción. Suscribite para recibir alertas.';
    openNotificationWithIcon('error', 'Sin stock disponible', description);
};

const initialState: CartState = {
    isLoading: false,
    hasLoaded: false,
    id: -1,
    cartItems: [],
    total: { totalSinDesc: 0, descuento: 0, total: 0 },
    hasTakeaway: false,
    hasShipping: false,
    status: 'open' as CartStatus,
};

type CartApiPayload = CartState & {
    has_takeaway?: boolean;
    has_shipping?: boolean;
    store_address?: CartState['storeAddress'] | NonNullable<CartState['storeAddress']>[number] | null;
};

const resolveCartFlag = (
    payload: CartApiPayload,
    camelKey: 'hasTakeaway' | 'hasShipping',
    snakeKey: 'has_takeaway' | 'has_shipping',
): boolean => {
    const value = payload[camelKey] ?? payload[snakeKey];
    return Boolean(value);
};

const resolveStoreAddress = (payload: CartApiPayload): CartState['storeAddress'] => {
    const value = payload.storeAddress ?? payload.store_address;
    if (!value) {
        return undefined;
    }
    return Array.isArray(value) ? value : [value];
};

/** Carga carrito inicial, completa detalles de cada línea.
 *    In: void (thunk)
 *    Out: CartState (fulfilled) / string (rejected) */
export const loadInitialCart = createAsyncThunk<CartState, void, { state: RootState }>(
    'cart/loadInitialCart',
    async (_, { rejectWithValue }) => {
        logInfo('Inicia loadInitialCart');
        let cartData: CartState;
        try {
            // Primero, obtén el estado del carrito completo.
            const cartResponse = await conns<CartState | null>('eshop:cart:handler').get();
            logInfo('loadInitialCart.data: ', cartResponse.data);
            cartData = cartResponse.data ?? { ...initialState };
            // cartData = {id: 1, total:{totalSinDesc: 0,descuento: 0, total:0}}
        } catch (error) {
            logError('#10002 Error al cargar el carrito de compras: ', error);
            return rejectWithValue('#10002 Error al cargar el carrito de compras.');
        }

        const updatedCartItems: CartItem[] = []; // Array para guardar los items actualizados
        try {
            for (const line of cartData.cartItems) {
                const itemResponse = await conns<Item>('eshop:catalogue:get_data_from_id', [line.item_id]).get();
                logInfo('productResponse.data: ', itemResponse.data);
                const lineOptions = sanitizeOptionArray(line.options ?? line.optionsInfo);
                // Crea un nuevo objeto para cada item y añádelo al array actualizado
                updatedCartItems.push({
                    ...line,
                    discount_labels: line.discount_labels || [],
                    options: lineOptions,
                    optionsInfo: lineOptions,
                    item: itemResponse.data,
                });
            }
            const initialData = { ...cartData, cartItems: updatedCartItems };
            logInfo('loadInitialCart - Items initialData: ', initialData);
            return initialData;

        } catch (error) {
            logError('#10003 Error al cargar el carrito de compras: ', error);
            return rejectWithValue('#10003 Error al cargar el carrito de compras.');
        }
    }
);

/** Agrega nueva línea al carrito (en BE) y retorna línea enriquecida para FE.
 *    In: { item: Item; quantity?: number; optionsInfo?: ItemOptionValue | ItemOptionInfo[] | null }
 *    Out: CartItem (fulfilled) / Error serializable (rejected) */
export const sendNewLineAsync = createAsyncThunk<
    CartItem,
    { item: Item; quantity?: number; optionsInfo?: ItemOptionValue | ItemOptionInfo[] | null },
    { state: RootState }
>(
    'cart/sendNewLineAsync',
    async ({ item, quantity = 1, optionsInfo }, { rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            if (state.cart.status !== 'open') {
                return rejectWithValue('#10005 Cart not editable');
            }
            const normalizedOptions = buildCartItemOptions(item, optionsInfo);
            const existing = state.cart.cartItems.find((l) => (
                l.item_id === item.id &&
                optionsAreEqual(
                    sanitizeOptionArray(l.options ?? l.optionsInfo),
                    normalizedOptions,
                )
            ));

            // obtener precio unitario desde Item (ajustar a modelo real si difiere)
            // 1) Obtener precio numérico de Item de forma robusta
            const unitPrice = parsePrice(item?.price);

            const basePayload = {
                item_id: item.id,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                main_item_id: (item as any)?.parent_id ?? item.id ?? 0,
                name: item.name,
            };

            let cartResponse;
            if (existing) {
                const newQty = existing.quantity + quantity;
                const fallbackOptions = sanitizeOptionArray(existing.options ?? existing.optionsInfo);
                const payloadOptions = normalizedOptions.length > 0 ? normalizedOptions : fallbackOptions;
                const payload = {
                    id: existing.id,
                    ...basePayload,
                    quantity: newQty,
                    discount_labels: [],
                    subtotal: {
                        totalSinDesc: Math.round((unitPrice?.value || 0) * newQty),
                        descuento: 0,
                        total: Math.round((unitPrice?.value || 0) * newQty),
                    },
                    options: payloadOptions,
                } satisfies Omit<CartItem, 'item' | 'optionsInfo'>;
                logInfo('Actualizando línea existente payload:', payload);
                cartResponse = await conns<CartItem>('eshop:cart:line_handler', [existing.id]).patch(payload);
            } else {
                const payloadOptions = normalizedOptions;
                const payload = {
                    id: Date.now(),
                    ...basePayload,
                    quantity,
                    discount_labels: [],
                    subtotal: {
                        totalSinDesc: Math.round((unitPrice?.value || 0) * quantity),
                        descuento: 0,
                        total: Math.round((unitPrice?.value || 0) * quantity),
                    },
                    options: payloadOptions,
                } satisfies Omit<CartItem, 'item' | 'optionsInfo'>;
                logInfo('Iniciando sendNewLine payload:', payload);
                cartResponse = await conns<CartItem>('eshop:cart:line_handler').post(payload);
            }

            logInfo('Respuesta del servidor - sendNewLine: ', cartResponse.data);

            const dataCartLine = cartResponse.data;
            const responseOptions = sanitizeOptionArray(dataCartLine.options ?? normalizedOptions);

            // 3) Notificación de éxito
            openNotificationWithIcon(
                'success',
                '¡Producto agregado!',
                'El producto ha sido agregado con éxito. ¿Te gustaría finalizar la compra?',
                (<Button key="Pagar" block href='/eshop/checkout'>Ir a pagar</Button>)
            );

            // 4) Para el estado local, agregamos info útil del Item y optionsInfo SOLO en FE
            return {
                ...dataCartLine,
                discount_labels: dataCartLine.discount_labels || [],
                item,
                options: responseOptions,
                optionsInfo: responseOptions,
            } as CartItem;
        } catch (error) {
            logError('Error al enviar la nueva línea de producto: ', error);
            if (isAxiosError(error)) {
                return rejectWithValue({
                    message: error.message,
                    response: {
                        status: error.response?.status,
                        data: error.response?.data,
                    },
                });
            }
            return rejectWithValue(error);
        }
    }
);

/** Actualiza cantidad de una línea y retorna índice y datos actualizados.
 *    In: { prodId:number; quantity:number }
 *    Out: { index:number; data:CartItem } / error serializable */
export const UpdateQtyCartLineAsync = createAsyncThunk(
    'cart/updateQtyCartLineAsync',
    async ({ prodId, quantity }: { prodId: number; quantity: number }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as RootState;
            if (state.cart.status !== 'open') {
                return rejectWithValue('#10005 Cart not editable');
            }
            logInfo('UpdateQtyCartLineAsync - state: ', state, 'state.cart.cartItems: ', state.cart.cartItems, 'prodId: ', prodId)
            const itemIndex = state.cart.cartItems.findIndex((line: CartItem) => line.item_id === prodId);
            if (itemIndex === -1) throw new Error("Item not found in the cart");
            const line = state.cart.cartItems[itemIndex];

            const safeQuantity = Math.max(1, Math.floor(quantity));
            const baseQuantity = Math.max(line.quantity, 1);
            const unitSubtotal = Math.round(line.subtotal.totalSinDesc / baseQuantity);
            const unitTotal = Math.round(line.subtotal.total / baseQuantity);
            const unitDiscount = Math.max(0, unitSubtotal - unitTotal);
            const payload: CartItem = {
                id: line.id,
                item_id: line.item_id,
                main_item_id: line.main_item_id,
                name: line.name,
                quantity: safeQuantity,
                discount_labels: line.discount_labels || [],
                options: sanitizeOptionArray(line.options ?? line.optionsInfo),
                subtotal: {
                    totalSinDesc: unitSubtotal * safeQuantity,
                    descuento: unitDiscount * safeQuantity,
                    total: unitTotal * safeQuantity,
                },
            };
            const responseCartItem = await conns<CartItem>('eshop:cart:line_handler', [line.id]).patch(payload);

            return { index: itemIndex, data: responseCartItem.data }; // Retorna el ID del producto eliminado para uso en el reducer
        } catch (error) {
            if (isAxiosError(error)) {
                // Solo devolver los datos serializables del error
                return rejectWithValue({
                    message: error.message,
                    response: {
                        status: error.response?.status,
                        data: error.response?.data
                    }
                });
            }
            return rejectWithValue(error);
        }
    }
);

/** Elimina una línea del carrito en BE y retorna el item_id eliminado.
 *    In: prodId:number
 *    Out: number (fulfilled) / error serializable */
export type RemoveFromCartPayload = {
    itemId: number;
    lineId: number;
};

const resolveCartLineForRemoval = (
    state: RootState,
    { itemId, lineId }: RemoveFromCartPayload,
): CartItem | undefined => {
    const directMatch = state.cart.cartItems.find((line) => line.id === lineId);
    if (directMatch) {
        return directMatch;
    }
    return state.cart.cartItems.find((line) => line.item_id === itemId);
};

/** Elimina una línea del carrito en BE y retorna el item/linea removidos. */
export const removeFromCartAsync = createAsyncThunk<
    RemoveFromCartPayload,
    RemoveFromCartPayload,
    { state: RootState }
>(
    'cart/removeFromCartAsync',
    async (payload, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            if (state.cart.status !== 'open') {
                return rejectWithValue('#10005 Cart not editable');
            }

            const line = resolveCartLineForRemoval(state, payload);
            if (!line) {
                throw new Error('Item not found in the cart');
            }

            const response = await conns('eshop:cart:line_handler', [line.id]).delete();
            logInfo('removeFromCartAsync - response: ', response, 'response.status: ', response.status);

            if (response.status < 200 || response.status >= 300) {
                throw new Error('Failed to delete the item from the server');
            }

            return { itemId: line.item_id, lineId: line.id } satisfies RemoveFromCartPayload;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/** Vacía el carrito en BE. In: void / Out: void o error serializable */
export const cleanCartAsync = createAsyncThunk<CleanCartResult, void, { state: RootState }>(
    'cart/cleanCartAsync',
    async (_, { rejectWithValue, getState, dispatch }) => {
        try {
            const state = getState() as RootState;
            if (state.cart.status !== 'open') {
                return rejectWithValue('#10005 Cart not editable');
            }
            // Realiza la petición DELETE al servidor
            const response = await conns<ClearCartResponsePayload>('eshop:cart:handler').delete();
            if (response.status < 200 || response.status >= 300) {
                throw new Error('#10004 Error al vaciar el carrito.');
            }
            const data = response.data ?? { status: 'unknown', cart_id: null };
            const normalized: CleanCartResult = {
                status: data.status ?? 'unknown',
                cartId: typeof data.cart_id === 'number' ? data.cart_id : null,
                reloaded: false,
            };

            if (normalized.status === 'cleared' || normalized.status === 'empty') {
                const reloadAction = await dispatch(loadInitialCart());
                normalized.reloaded = loadInitialCart.fulfilled.match(reloadAction);
                if (!normalized.reloaded && loadInitialCart.rejected.match(reloadAction)) {
                    logError('No se pudo recargar el carrito después de vaciarlo', reloadAction.payload ?? reloadAction.error);
                }
            }
            return normalized;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/** Aplica un voucher y retorna el estado actualizado del carrito.
 *    In: code:string / Out: CartState o error serializable */
export const applyVoucherAsync = createAsyncThunk<CartState, string, { state: RootState }>(
    'cart/applyVoucherAsync',
    async (voucherCode, { rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            if (state.cart.status !== 'open') {
                return rejectWithValue('#10005 Cart not editable');
            }
            const response = await conns<CartState>('eshop:cart:voucher').post({ code: voucherCode });
            if (response.status === 200)
                return response.data; // Se asume que el backend retorna el estado actualizado del carrito
            throw new Error('Failed to apply voucher');
        } catch (error) {
            if (isAxiosError(error)) {
                return rejectWithValue({
                    message: error.message,
                    response: {
                        status: error.response?.status,
                        data: error.response?.data,
                    },
                });
            }
            return rejectWithValue(error);
        }
    }
);

/** Quita un voucher y retorna el estado actualizado del carrito.
 *    In: code:string / Out: CartState o error serializable */
export const removeVoucherAsync = createAsyncThunk<CartState, string, { state: RootState }>(
    'cart/removeVoucherAsync',
    async (voucherCode, { rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            if (state.cart.status !== 'open') {
                return rejectWithValue('#10005 Cart not editable');
            }
            const response = await conns<CartState>('eshop:cart:voucher').delete({ code: voucherCode });
            if (response.status === 200) return response.data;
            throw new Error('Failed to remove voucher');
        } catch (error) {
            if (isAxiosError(error)) {
                return rejectWithValue({
                    message: error.message,
                    response: {
                        status: error.response?.status,
                        data: error.response?.data,
                    },
                });
            }
            return rejectWithValue(error);
        }
    }
);


/**
 * Recalcula los totales del carrito partiendo de las líneas actuales.
 *
 * - Ignora items sin subtotal válido.
 * - Asegura que los importes sean numéricos para evitar NaN.
 * - Normaliza que el descuento nunca sea negativo.
 */
export const updateTotals = (state: CartState) => {
    const totals = state.cartItems.reduce(
        (acc, item) => {
            const lineTotalSinDesc = Number(item.subtotal?.totalSinDesc) || 0;
            const lineTotal = Number(item.subtotal?.total) || 0;

            acc.totalSinDesc += lineTotalSinDesc;
            acc.total += lineTotal;
            acc.descuento += Math.max(0, lineTotalSinDesc - lineTotal);
            return acc;
        },
        { totalSinDesc: 0, total: 0, descuento: 0 },
    );

    state.total.totalSinDesc = totals.totalSinDesc;
    state.total.total = totals.total;
    state.total.descuento = totals.descuento;
};


const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        /** Guarda valores por defecto (fecha/datetime) para opciones.
         *    In: CartDefaults / Out: void */
        setDefaultsValues(state, action: PayloadAction<CartDefaults>) {
            state.defaults = action.payload;
            logInfo('state.defaults: ', state.defaults, 'action.payload: ', action.payload);
        },
        resetCartState(state) {
            state.isLoading = false;
            state.hasLoaded = false;
            state.error = undefined;
            state.id = initialState.id;
            state.cartItems = [];
            state.vouchers = [];
            state.total = { totalSinDesc: 0, descuento: 0, total: 0 };
            state.hasTakeaway = false;
            state.hasShipping = false;
            state.storeAddress = undefined;
            state.status = 'open';
            state.defaults = undefined;
        },
        /** Agrega/actualiza línea localmente (no BE). In: CartItem / Out: void */
        addToCart(state, action: PayloadAction<CartItem>) {
            const itemIndex = state.cartItems.findIndex(i => i.item_id === action.payload.item_id);
            if (itemIndex !== -1) {
                const prev = state.cartItems[itemIndex];
                const unit = Math.round(prev.subtotal.total / Math.max(prev.quantity, 1));
                prev.quantity += action.payload.quantity ?? 1;
                prev.subtotal = {
                    totalSinDesc: unit * prev.quantity,
                    descuento: 0,
                    total: unit * prev.quantity,
                };
            } else {
                state.cartItems.push(action.payload); // Agrega un nuevo producto si no existe
            }
            logInfo('Actual state snapshot:', current(state.cartItems), 'action.payload: ', action.payload);
            updateTotals(state);
        },
        /** Reemplaza todas las líneas del carrito. In: CartItem[] / Out: void */
        updateAllCartItemsCart(state, action: PayloadAction<CartItem[]>) {
            state.cartItems = action.payload;
            updateTotals(state);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadInitialCart.fulfilled, (state, action: PayloadAction<CartState>) => {
                logInfo('Previous state:', current(state));
                logInfo('New Data:', action.payload);
                const payload = action.payload as CartApiPayload;
                state.isLoading = false;
                state.hasLoaded = true;
                state.id = payload.id;
                state.cartItems = payload.cartItems;
                state.vouchers = payload.vouchers;
                state.total = payload.total;
                state.hasTakeaway = resolveCartFlag(payload, 'hasTakeaway', 'has_takeaway');
                state.hasShipping = resolveCartFlag(payload, 'hasShipping', 'has_shipping');
                state.storeAddress = resolveStoreAddress(payload);
                state.status = payload.status ?? 'open';
            })


            .addCase(loadInitialCart.pending, (state) => {
                state.isLoading = true;
                logInfo('state.isLoading = true');
            })
            .addCase(loadInitialCart.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
                logInfo('state.isLoading = false, error: ', action.payload as string);
            });

        // Agrega una nueva línea.
        builder
            .addCase(sendNewLineAsync.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(sendNewLineAsync.fulfilled, (state, action: PayloadAction<CartItem>) => {
                state.isLoading = false;
                const idx = state.cartItems.findIndex(i => i.item_id === action.payload.item_id);

                if (idx !== -1) {
                    // sumamos cantidad y recalculamos subtotal de la línea
                    const prev = state.cartItems[idx];
                    const mergedOptions = sanitizeOptionArray(
                        action.payload.options ?? action.payload.optionsInfo ?? prev.options ?? prev.optionsInfo,
                    );
                    state.cartItems[idx] = {
                        ...prev,
                        ...action.payload,
                        item: action.payload.item ?? prev.item,
                        options: mergedOptions,
                        optionsInfo: mergedOptions,
                    };
                } else {
                    const normalizedOptions = sanitizeOptionArray(action.payload.options ?? action.payload.optionsInfo);
                    state.cartItems.push({
                        ...action.payload,
                        options: normalizedOptions,
                        optionsInfo: normalizedOptions,
                    });
                }

                updateTotals(state);
                logInfo('Producto agregado al carrito con éxito');
            })
            .addCase(sendNewLineAsync.rejected, (state, action) => {
                state.isLoading = false;
                logError('Error al agregar el producto al carrito:', action.payload);
                const stockError = extractStockErrorDetail(action.payload);
                if (stockError) {
                    notifyStockBlocked(stockError);
                    state.error = stockError;
                } else {
                    state.error = action.payload as string | ErrorResponseState;
                }
            });

        builder.addCase(UpdateQtyCartLineAsync.fulfilled, (state, action: PayloadAction<{ index: number, data: CartItem }>) => {
            state.isLoading = false;
            logInfo('New LineData:', action.payload);
            state.cartItems[action.payload.index].quantity = action.payload.data.quantity;
            state.cartItems[action.payload.index].subtotal = action.payload.data.subtotal;
            state.cartItems[action.payload.index].discount_labels = action.payload.data.discount_labels;
            const normalizedOptions = sanitizeOptionArray(
                action.payload.data.options
                ?? action.payload.data.optionsInfo
                ?? state.cartItems[action.payload.index].options
                ?? state.cartItems[action.payload.index].optionsInfo,
            );
            state.cartItems[action.payload.index].options = normalizedOptions;
            state.cartItems[action.payload.index].optionsInfo = normalizedOptions;
            updateTotals(state);
        })
            .addCase(UpdateQtyCartLineAsync.pending, (state) => {
                state.isLoading = true;
                logInfo('state.isLoading = true');
            });
        builder.addCase(UpdateQtyCartLineAsync.rejected, (state, action) => {
            state.isLoading = false;
            const stockError = extractStockErrorDetail(action.payload);
            if (stockError) {
                notifyStockBlocked(stockError);
                state.error = stockError;
            } else {
                const errorData = action.payload ? (action.payload as ErrorResponseState) : undefined;
                const responseData = errorData?.response?.data;
                state.error = (responseData ?? undefined) as typeof state.error;
                logInfo('state.isLoading = false,  error msj: ', errorData?.message, 'errors: ', errorData?.response?.data);
            }
        });
        builder.addCase(removeFromCartAsync.fulfilled, (state, action: PayloadAction<RemoveFromCartPayload>) => {
            logInfo('removeFromCartAsync.fulfilled', action.payload);
            state.isLoading = false;
            state.error = undefined;
            state.cartItems = state.cartItems.filter((item) => item.id !== action.payload.lineId);
            updateTotals(state);
        });
        builder.addCase(removeFromCartAsync.pending, (state) => {
            state.isLoading = true;
            logInfo('state.isLoading = true');
        });
        builder.addCase(removeFromCartAsync.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload as string;
            logInfo('state.isLoading = false, error: ', action.payload as string);
        });
        builder.addCase(cleanCartAsync.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(cleanCartAsync.fulfilled, (state, action: PayloadAction<CleanCartResult>) => {
            state.isLoading = false;
            state.hasLoaded = true;
            state.error = undefined;
            state.cartItems = [];
            state.total = { totalSinDesc: 0, descuento: 0, total: 0 };
            state.vouchers = [];
            state.hasTakeaway = false;
            state.hasShipping = false;
            state.storeAddress = undefined;
            state.id = action.payload.cartId ?? -1;
            state.status = 'open';
        });
        builder.addCase(cleanCartAsync.rejected, (state, action) => {
            state.isLoading = false;
            state.error = (action.payload ?? action.error ?? undefined) as typeof state.error;
        });

        // Casos para aplicar voucher
        builder.addCase(applyVoucherAsync.fulfilled, (state, action: PayloadAction<CartState>) => {
            state.isLoading = false;
            state.vouchers = action.payload.vouchers;
            state.total = action.payload.total;

            // Update 'quantity' and 'subtotal' for each cart item without overwriting the entire array
            state.cartItems = state.cartItems.map((item) => {
                // Find the corresponding item in the payload
                const updatedItem = action.payload.cartItems.find((i) => i.id === item.id);
                if (updatedItem) {
                    const normalizedOptions = sanitizeOptionArray(
                        updatedItem.options ?? updatedItem.optionsInfo ?? item.options ?? item.optionsInfo,
                    );
                    // Return a new object with updated fields
                    return {
                        ...item,
                        quantity: updatedItem.quantity,
                        subtotal: updatedItem.subtotal,
                        options: normalizedOptions,
                        optionsInfo: normalizedOptions,
                    };
                }
                // If no update is found, return the original item
                return item;
            });

            // Mostrar notificación de éxito
            openNotificationWithIcon('success', 'Voucher aplicado', 'El voucher se aplicó correctamente.');
            logInfo('Voucher aplicado exitosamente');
        }).addCase(applyVoucherAsync.pending, (state) => {
            state.isLoading = true;
        }).addCase(applyVoucherAsync.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload as ErrorResponseState;

            // Mostrar notificación de error
            logError('Error al aplicar el voucher:', state.error);
            openNotificationWithIcon('error', 'Error al aplicar voucher', state.error.response.data?.error as string || 'No se pudo aplicar el voucher.');
        });

        // Casos para remover voucher
        builder.addCase(removeVoucherAsync.fulfilled, (state, action: PayloadAction<CartState>) => {
            state.isLoading = false;
            state.vouchers = action.payload.vouchers;
            state.total = action.payload.total;

            // Update 'quantity' and 'subtotal' for each cart item without overwriting the entire array
            state.cartItems = state.cartItems.map((item) => {
                // Find the corresponding item in the payload
                const updatedItem = action.payload.cartItems.find((i) => i.id === item.id);
                if (updatedItem) {
                    const normalizedOptions = sanitizeOptionArray(
                        updatedItem.options ?? updatedItem.optionsInfo ?? item.options ?? item.optionsInfo,
                    );
                    // Return a new object with updated fields
                    return {
                        ...item,
                        quantity: updatedItem.quantity,
                        subtotal: updatedItem.subtotal,
                        options: normalizedOptions,
                        optionsInfo: normalizedOptions,
                    };
                }
                // If no update is found, return the original item
                return item;
            });

            // Mostrar notificación de éxito
            openNotificationWithIcon('success', 'Voucher removido', 'El voucher se removió correctamente.');
            logInfo('Voucher removido exitosamente');
        }).addCase(removeVoucherAsync.pending, (state) => {
            state.isLoading = true;
        }).addCase(removeVoucherAsync.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload as ErrorResponseState;

            // Mostrar notificación de error
            openNotificationWithIcon('error', 'Error al remover voucher', state.error.response.data?.error as string || 'No se pudo remover el voucher.');
            logError('Error al remover el voucher:', state.error);
        });
    }
});

export const { setDefaultsValues, addToCart, updateAllCartItemsCart, resetCartState
} = cartSlice.actions;
export default cartSlice.reducer;
